import firebaseConfig from '@/firebase.config';
import { useAuthStore } from '@/modules/auth/store/auth';
import useFirebaseStore from '@/store/firebase';
import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { storeToRefs } from 'pinia';
import { v4 as uuidv4 } from 'uuid';

export const useFirebase = () => {
  const runtimeConfig = useRuntimeConfig();

  const app = ref();
  const initMessaging = ref(false);
  const eventsBound = ref(false);
  const vapidKey = runtimeConfig.public.firebaseVapidKey;

  const { hasDevice, registerDevice } = useDevice();
  const { messaging, hasMessaging } = storeToRefs(useFirebaseStore());
  const { getUser, hasLoggedInUser } = storeToRefs(useAuthStore());
  const { showSnackbar } = useSnackbar();

  async function initialize(): Promise<void> {
    if (
      import.meta.server ||
      !vapidKey ||
      !hasLoggedInUser.value ||
      initMessaging.value
    )
      return;

    initMessaging.value = true;
    try {
      if (!(await checkPermission())) return;
      createApp();
      createMessaging();
      await createToken();
    } finally {
      initMessaging.value = false;
    }
  }

  function createApp(): void {
    if (app.value || hasMessaging.value) return;

    try {
      app.value = initializeApp(firebaseConfig);
    } catch (error) {
      console.error('Firebase App Plugin Error: ', error);
    }
  }

  function createMessaging(): void {
    if (hasMessaging.value) return;

    try {
      messaging.value = getMessaging(app.value);
    } catch (error) {
      console.error('Firebase Messaging Error: ', error);
    }
  }

  async function createToken(): Promise<void> {
    if (!hasMessaging.value) return;

    if (await isSupported()) {
      await getToken(messaging.value, {
        vapidKey: vapidKey,
      }).then(async (currentToken) => {
        if (currentToken) {
          const storageKey = 'tradel.firebase.device-id';
          const deviceId = window.localStorage.getItem(storageKey) || uuidv4();
          window.localStorage.setItem(storageKey, deviceId);
          await registerDevice(currentToken, deviceId);

          if (hasDevice.value) {
            bindEvents();
          }
        } else {
          checkPermission();
        }
      });
    } else {
      errorMessage();
    }
  }

  function bindEvents(): void {
    if (hasMessaging.value && !eventsBound.value) {
      eventsBound.value = true;
      /**
       * Handle incoming messages. Called when:
       * - a message is received while the app has focus
       * - the user clicks on an app notification created by a service worker
       * `messaging.setBackgroundMessageHandler` handler.
       *
       * @TESTING
       * To test it out, we can go and use the Firebase Messaging > Campaign creation.
       * On the additional options section, it needs to have 2 custom fields (body and title)
       * for the messages to be properly received
       */
      onMessage(messaging.value, (payload) => {
        const data = payload.data ?? {};
        const title =
          payload.notification?.title || data.title || 'Order update';
        const baseBody =
          payload.notification?.body || data.body || 'An order was updated.';
        const body =
          data.type === 'order.cancelled' && data.rejection_reason
            ? `${baseBody} ${data.rejection_reason}`
            : baseBody;
        const orderId = data.order_id;

        if (data.type === 'order.cancelled' && orderId) {
          window.dispatchEvent(
            new CustomEvent('tradel:order-cancelled', { detail: data })
          );
        }

        showSnackbar({
          text: `${title}. ${body}`,
          sign: 'mdi-bell-outline',
          delay: 8000,
          ...(orderId
            ? {
                actionLabel: 'View order',
                action: () =>
                  navigateTo(
                    getUser.value.account_type === 'Merchant'
                      ? `/merchant/orders/${orderId}`
                      : `/orders/${orderId}`
                  ),
              }
            : {}),
        });
      });
    }
  }

  async function checkPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      errorMessage();
      return false;
    } else if (Notification.permission === 'granted') {
      return true;
    } else if (Notification.permission === 'denied') {
      return false;
    }

    return (await Notification.requestPermission()) === 'granted';
  }

  function errorMessage(): void {
    showSnackbar({
      text: `This browser does not support desktop notification`,
      state: false,
    });
  }

  watch(
    hasLoggedInUser,
    (loggedIn) => {
      if (loggedIn) initialize();
    },
    { immediate: true }
  );
};
